import { bool, func } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';
import cx from 'classnames';
import { enablePageScroll } from 'scroll-lock';

import Button from '../../Button';
import { ReactComponent as PostIcon } from '../assets/post.svg';
import { keyCodes } from '../../../constants';

import s from './index.module.css';
import { setCustomVh } from '../../../utils';
import { SET_OPEN_KEYBOARD } from '../../../store/actions/meta';
import { getIsTyping, getMessageButtons, getMessagesItems } from '../../../store/selectors';

const ON_TYPE_DEBOUNCE = 300;
const MIN_ROWS = 1;
const MAX_ROWS = 12;

const BoxMessageInput = ({ sendMessage, onType, onSend, onFocus, isWidgetPath }) => {
  const dispatch = useDispatch();
  const isTyping = useSelector(getIsTyping);
  const buttons = useSelector(getMessageButtons);
  const items = useSelector(getMessagesItems) || [];
  const inputEl = useRef();
  const [rows, setRows] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const hasButtons = buttons && buttons.length > 0;
  const isShowFeedback = items && items.find((message) => message.content && message.content.showRateComment);

  useDebounce(
    () => {
      if (onType && inputValue) {
        onType(true);
      } else if (isTyping && onType && !inputValue) {
        onType(false);
      }
    },
    ON_TYPE_DEBOUNCE,
    [inputValue],
  );

  const handleFocus = (e) => {
    onFocus();
    setTimeout(() => {
      setCustomVh();
      dispatch({ type: SET_OPEN_KEYBOARD, payload: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleBlur = (e) => {
    enablePageScroll();
    setTimeout(() => {
      setCustomVh();
      dispatch({ type: SET_OPEN_KEYBOARD, payload: false });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 80);
  };

  useEffect(() => {
    if (inputEl.current) {
      inputEl.current.focus();
      handleFocus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (inputEl.current && hasButtons) {
      inputEl.current.blur();
    }
  }, [hasButtons]);

  const handleSend = () => {
    const currentInput = inputEl && inputEl.current;
    const text = currentInput && currentInput.value;

    if (text && /\S/.test(text)) {
      sendMessage(text);
      currentInput.value = '';
      setRows(MIN_ROWS);

      if (onSend) {
        onSend();
      }
    }
  };

  const handleTyping = (e) => {
    const inputValue = e.target.value;
    setInputValue(inputValue);
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === keyCodes.ENTER && !e.shiftKey) {
      handleSend();
      if (e.preventDefault) {
        e.preventDefault();
      }
    }
  };

  const handleChange = (e) => {
    const textareaLineHeight = 22;

    const previousRows = e.target.rows;
    e.target.rows = MIN_ROWS;

    const currentRows = Math.floor((e.target.scrollHeight - textareaLineHeight) / textareaLineHeight);

    if (currentRows === previousRows) {
      e.target.rows = currentRows;
    }

    if (currentRows >= MAX_ROWS) {
      e.target.rows = MAX_ROWS;
      e.target.scrollTop = e.target.scrollHeight;
    }

    setRows(currentRows < MAX_ROWS ? currentRows : MAX_ROWS);
  };

  const handleMouseDown = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
  };

  if (isShowFeedback) {
    return null;
  }

  return (
    <div className={cx(s.wrapper, { [s.widgetInputWrapper]: isWidgetPath })} data-scroll-lock-scrollable>
      <textarea
        type="text"
        className={s.input}
        placeholder="Ваше сообщение"
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        onKeyUp={handleTyping}
        onFocus={handleFocus}
        onClick={handleFocus}
        onBlur={handleBlur}
        ref={inputEl}
        rows={rows || 1}
      />
      <Button className={s.postButton} onClick={handleSend} onMouseDown={handleMouseDown}>
        <PostIcon />
      </Button>
    </div>
  );
};

BoxMessageInput.propTypes = {
  sendMessage: func,
  onFocus: func,
  isTyping: bool,
  onType: func,
};

BoxMessageInput.defaultProps = {
  sendMessage: () => {},
  onType: () => {},
  onFocus: () => {},
  isTyping: false,
};

export default BoxMessageInput;
